import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { strings } from '../lib/strings';
import { AccountButton } from './AccountButton';

function Wordmark({ tone, className = '' }: { tone: 'dark' | 'light'; className?: string }) {
  return (
    <Link
      to="/"
      className={`pressable shrink-0 rounded-sm text-lg font-semibold tracking-tight ${tone === 'dark' ? 'text-white' : 'text-primary'} ${className}`}
    >
      {strings.brandFirst}{' '}
      <span className={tone === 'dark' ? 'text-white/50' : 'text-subtle'}>{strings.brandSecond}</span>
    </Link>
  );
}

/** The bar only tucks away once the reader is well into the wall. */
const TUCK_AFTER_PX = 320;
const TUCK_SLOP_PX = 6;

interface Props {
  /** Full masthead on first entry; compact bar only elsewhere. */
  variant: 'masthead' | 'compact';
  renderSearch: (size: 'hero' | 'compact') => ReactNode;
  renderSort: (tone: 'dark' | 'light') => ReactNode;
}

/*
 * The compact bar is sticky rather than fixed: a fixed bar would detach from
 * the wall when it recedes behind an open viewer sheet (transformed ancestors
 * re-anchor fixed children). In masthead mode it overlaps the masthead via a
 * negative margin equal to its height, so it takes no layout space.
 */
export function UniverseHeader({ variant, renderSearch, renderSort }: Props) {
  const mastheadRef = useRef<HTMLElement>(null);
  const [mastheadVisible, setMastheadVisible] = useState(variant === 'masthead');

  useEffect(() => {
    const el = mastheadRef.current;
    if (variant !== 'masthead' || !el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setMastheadVisible(Boolean(entry?.isIntersecting)),
      { rootMargin: '-72px 0px 0px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [variant]);

  const barRef = useRef<HTMLDivElement>(null);
  const [tucked, setTucked] = useState(false);

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const bar = barRef.current;
      const busy = bar && (bar.contains(document.activeElement) || bar.querySelector('[aria-expanded="true"]'));
      if (y < TUCK_AFTER_PX || busy || y < lastY - TUCK_SLOP_PX) setTucked(false);
      else if (y > lastY + TUCK_SLOP_PX) setTucked(true);
      if (Math.abs(y - lastY) > TUCK_SLOP_PX) lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const compactVisible = variant === 'compact' || !mastheadVisible;

  return (
    <>
      <div
        ref={barRef}
        onFocusCapture={() => setTucked(false)}
        className={`sticky top-0 z-header h-16 border-b border-line bg-material backdrop-blur-2xl backdrop-saturate-[1.4] transition-[opacity,transform,visibility] duration-slow ease-expo ${
          variant === 'masthead' ? '-mb-16' : ''
        } ${
          !compactVisible
            ? 'invisible -translate-y-2 opacity-0'
            : tucked
              ? 'visible -translate-y-full opacity-100'
              : 'visible translate-y-0 opacity-100'
        }`}
      >
        <div className="mx-auto flex h-full max-w-page items-center gap-3 px-[var(--page-gutter)] sm:gap-6">
          <Link
            to="/"
            aria-label={strings.brand}
            data-cursor-stick
            className="pressable flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-primary text-white sm:hidden"
          >
            <svg viewBox="0 0 20 20" aria-hidden="true" className="h-5 w-5" fill="none">
              <circle cx="10" cy="10" r="3.4" fill="currentColor" />
              <ellipse cx="10" cy="10" rx="7.6" ry="2.9" transform="rotate(-24 10 10)" stroke="currentColor" strokeWidth="1.3" strokeOpacity="0.75" />
            </svg>
          </Link>
          <Wordmark tone="light" className="hidden sm:block" />
          <div className="min-w-0 flex-1 sm:max-w-search">{renderSearch('compact')}</div>
          <div className="ml-auto flex items-center gap-2">
            {renderSort('light')}
            <AccountButton tone="light" />
          </div>
        </div>
      </div>

      {variant === 'masthead' && (
        <header
          ref={mastheadRef}
          className="relative z-[1] bg-brand-deep pt-[env(safe-area-inset-top)]"
          style={{
            backgroundImage:
              'radial-gradient(80% 140% at 50% -20%, rgba(47,111,228,0.32) 0%, rgba(0,26,77,0) 60%), radial-gradient(40% 80% at 90% 120%, rgba(255,255,255,0.06) 0%, rgba(0,26,77,0) 70%)',
          }}
        >
          <div className="mx-auto flex max-w-page items-center justify-between px-[var(--page-gutter)] pt-6">
            <Wordmark tone="dark" />
            <div className="flex items-center gap-2">
              {renderSort('dark')}
              <AccountButton tone="dark" />
            </div>
          </div>
          <div className="masthead-drift mx-auto max-w-reading px-4 pb-12 pt-8 sm:px-8 sm:pb-16 sm:pt-12">
            <div className="rise-in mx-auto max-w-search" style={{ animationDelay: '80ms' }}>
              {renderSearch('hero')}
            </div>
          </div>
        </header>
      )}
    </>
  );
}
